import { motion } from "framer-motion";
import { ChevronRight, Play, Sparkles, Star, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStats } from "@/hooks/useStats";
import { useI18n } from "@/contexts/I18nContext";

const HeroSection = () => {
  const { stats, loading } = useStats();
  const { t } = useI18n();

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(0)}M+`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K+`;
    }
    return num.toString();
  };

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute top-1/4 left-10 w-72 h-72 bg-primary/20 rounded-full blur-[100px] animate-pulse" />
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: "1s" }} />
      
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="text-center space-y-8">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 glass-card text-sm font-medium"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-muted-foreground">{t('hero.trustedBy', { count: loading ? '...' : formatNumber(stats?.servers || 0) })}</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight"
          >
            <span className="text-foreground">{t('hero.titleTop')}</span>
            <br />
            <span className="gradient-text">{t('hero.titleBottom')}</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto text-balance"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
          >
            <Button size="lg" className="btn-gradient border-0 text-lg px-8 py-6 h-auto group">
              <Zap className="w-5 h-5 mr-2 group-hover:animate-pulse" />
              {t('hero.addDiscord')}
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6 h-auto border-border/50 hover:bg-muted group">
              <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
              {t('hero.watchDemo')}
            </Button>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex items-center justify-center gap-8 pt-12 text-muted-foreground"
          >
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary border-2 border-background"
                  />
                ))}
              </div>
              <span className="text-sm">{t('hero.servers', { count: loading ? '...' : formatNumber(stats?.servers || 0) })}</span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 text-discord-yellow fill-discord-yellow" />
                ))}
              </div>
              <span className="text-sm">{stats?.botStatus.online ? t('hero.online') : t('hero.offline')}</span>
            </div>
          </motion.div>
        </div>

        {/* Hero Image/Preview */}
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-20 relative"
        >
          <div className="glass-card p-2 md:p-4 glow-primary">
            <div className="relative rounded-xl overflow-hidden bg-background aspect-video">
              <DashboardPreview />
            </div>
          </div>
          
          {/* Floating Elements */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-8 -right-8 glass-card p-4 hidden lg:block"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-discord-green to-accent flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold">{t('hero.automodActive')}</p>
                <p className="text-xs text-muted-foreground">{t('hero.threatsBlocked', { count: 12 })}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -bottom-4 -left-8 glass-card p-4 hidden lg:block"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Star className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold">{t('hero.levelUp')}</p>
                <p className="text-xs text-muted-foreground">{t('hero.levelReached')}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const DashboardPreview = () => (
  <div className="w-full h-full bg-gradient-to-br from-card to-background p-6">
    <div className="grid grid-cols-12 gap-4 h-full">
      {/* Sidebar Preview */}
      <div className="col-span-3 bg-muted/30 rounded-lg p-4 space-y-3">
        <div className="h-8 bg-primary/20 rounded-lg" />
        <div className="h-6 bg-muted/50 rounded w-3/4" />
        <div className="h-6 bg-muted/50 rounded w-2/3" />
        <div className="h-6 bg-muted/50 rounded w-4/5" />
        <div className="h-6 bg-primary/30 rounded w-full" />
        <div className="h-6 bg-muted/50 rounded w-3/4" />
      </div>
      
      {/* Main Content Preview */}
      <div className="col-span-9 space-y-4">
        <div className="flex gap-4">
          <div className="flex-1 bg-muted/30 rounded-lg p-4">
            <div className="h-4 bg-muted/50 rounded w-1/3 mb-2" />
            <div className="h-8 bg-primary/30 rounded w-1/2" />
          </div>
          <div className="flex-1 bg-muted/30 rounded-lg p-4">
            <div className="h-4 bg-muted/50 rounded w-1/3 mb-2" />
            <div className="h-8 bg-secondary/30 rounded w-2/3" />
          </div>
          <div className="flex-1 bg-muted/30 rounded-lg p-4">
            <div className="h-4 bg-muted/50 rounded w-1/3 mb-2" />
            <div className="h-8 bg-accent/30 rounded w-1/2" />
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4 flex-1">
          <div className="h-4 bg-muted/50 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-muted/40 rounded-lg p-3 space-y-2">
                <div className="h-8 w-8 bg-primary/30 rounded-lg" />
                <div className="h-3 bg-muted/50 rounded w-3/4" />
                <div className="h-2 bg-muted/30 rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default HeroSection;
